import React from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from './config/stripe';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import CEOPortal from './components/CEOPortal';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ClassesPage from './pages/ClassesPage';
import CommunicationPage from './pages/CommunicationPage';
import DashboardPage from './pages/DashboardPage';
import EmployeeManagementPage from './pages/EmployeeManagementPage';
import EmployeeOnboardingPage from './pages/EmployeeOnboardingPage';
import GymRegistrationPage from './pages/GymRegistrationPage';
import GymSettingsPage from './pages/GymSettingsPage';
import HandbookPage from './pages/HandbookPage';
import LoginPage from './pages/LoginPage';
import MembersPage from './pages/MembersPage';
import ReportsPage from './pages/ReportsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import WorkoutsPage from './pages/WorkoutsPage';
import AuthCallbackPage from './pages/AuthCallbackPage';

// Protected Route Component (currently not used but available for future use)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  const { isAuthenticated, user, isLoading } = useAuth();

  // Wait for auth check to complete before rendering routes
  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    // Check if user is CEO
    if (user?.is_ceo) {
      return (
        <Routes>
          <Route path="/" element={<Navigate to="/ceo" replace />} />
          <Route path="/ceo/*" element={<CEOPortal />} />
          <Route path="*" element={<Navigate to="/ceo" replace />} />
        </Routes>
      );
    }

    // Regular admin portal for non-CEO users
    return (
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/classes" element={<ClassesPage />} />
          <Route path="/workouts" element={<WorkoutsPage />} />
          <Route path="/members" element={<MembersPage />} />
          <Route path="/employee-management" element={<EmployeeManagementPage />} />
          <Route path="/gym-settings" element={<GymSettingsPage />} />
          <Route path="/communication" element={<CommunicationPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/handbook" element={<HandbookPage />} />
          {/* Billing page removed - CEO manages all subscriptions */}
          <Route path="/gym-registration" element={<GymRegistrationPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/employee-onboarding" element={<EmployeeOnboardingPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Elements stripe={getStripe()}>
          <Router>
            <AppRoutes />
          </Router>
        </Elements>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
